/**
 * IdP providers: the catalog-driven auth-provider CRUD surface of the Identity providers page.
 *
 * | # | endpoint (what the SPA calls) | producer permission | response |
 * |---|---|---|---|
 * | I1 | `GET /v1/admin/auth-providers/catalog` | `cp:idp:read` | `IdpProviderCatalogEntry[]` (bare array) |
 * | I2 | `GET /v1/admin/auth-providers` | `cp:idp:read` | `IdpProvider[]` (bare array) |
 * | I3 | `POST /v1/admin/auth-providers` | `cp:idp:create` | 201; 400 unknown type or missing required field; 409 `Provider type '<t>' is already configured` |
 * | I4 | `PATCH /v1/admin/auth-providers/:id` | `cp:idp:update` | 200; 404; 503 (see below) |
 * | I5 | `DELETE /v1/admin/auth-providers/:id` | `cp:idp:delete` | 204, no body; 404; 503 |
 * | I6 | `GET /v1/admin/auth-providers/identity-token` | `cp:idp:read` | `IdentityIssuerConfig`; 502 when cp-auth-api answers outside the contract |
 *
 * The site-admin proxy forwards the path unchanged, so the path the browser calls is the
 * producer's path. Unlike `permissions/grants` and `permissions/groups/:id/members`, there is no
 * rename to keep in sync here.
 *
 * ## A 503 on this surface can be a refused rule, and the browser cannot tell it from an outage
 *
 * cp-auth-api throws `UnprocessableEntityException` (422) for the two rules an operator hits:
 * "Cannot disable the last enabled auth provider" (`AuthProviderService.updateProvider`) and
 * "Cannot delete the last enabled auth provider" (`AuthProviderService.deleteProvider`).
 * `PermissionsProxyService` in site-admin-api maps 400, 403, 404 and 409 to their own exceptions and
 * every other status to `ServiceUnavailableException`: a 503 whose message is the fixed
 * `cp-auth-api unavailable (ref <uuid>)`. It drops the upstream text.
 *
 * So both rules reach the browser as a 503 with no reason, the same body as a real outage. Never
 * branch on a 422 here: it never reaches the browser. The page names the rule as one cause of a 503
 * from a toggle or a delete and shows the server's reference (`writeFailureMessage` in
 * `panes/idp-model.ts`, decision J4 of plan 2026-10-22-console-ward-identity-providers). The proxy
 * defect itself is a site-admin-api change.
 *
 * ## There is no `SECRET_MASK` in this module, and its absence is the decision (06a D-17)
 *
 * The producer masks secret config values and emits a structured companion flag, `has<Field>: true`
 * (`AuthProviderService.maskSecrets`, contract `idp-provider.ts`). This module reads that flag and
 * never compares against a mask literal. Three reasons, any one sufficient:
 *
 * 1. The mask does not carry preservation. `AuthProviderService.mergeConfig` is
 *    `{ ...existing, ...incoming }`, so an omitted key already keeps the stored value. Its
 *    mask-restore branch is a second, redundant route to the same outcome.
 * 2. This package already holds that literal with the opposite meaning.
 *    `DESTINATION_SECRET_MASK` in `api/alerting-wire.ts` belongs to a different producer where the
 *    mask is no preserve token, and `AlertsPane` opens the field empty so the operator never
 *    "keeps" a string that is not their credential. Two identical glyph runs, two producers, two
 *    meanings: a third copy invites someone to harmonise them, and that breaks one screen.
 * 3. A boolean cannot drift on a homoglyph and needs no byte comparison.
 *
 * The trap this closes: `maskSecrets` adds `has<Field>` to the response, and `mergeConfig` is a
 * shallow spread. A form that built its `config` by a spread of `provider.config` would send
 * `hasClientSecret: true`, and the merge would store that derived key in the jsonb. The form builds
 * `config` from form state only, over the catalog's fields; `has<Field>` is no catalog field.
 */
import {
  identityIssuerConfigSchema,
  type CreateIdpProviderDto,
  type IdentityIssuerConfig,
  type IdentityIssuerKeyVersion,
  type IdpProvider,
  type IdpProviderCatalogEntry,
  type IdpProviderConfig,
  type IdpProviderConfigField,
  type UpdateIdpProviderDto,
} from '@zion/sdk-contracts';
import { api } from '../platform/http/client';
import { hasSecretFlagKey } from './auth-provider-secret-flag';

/**
 * Re-exported as types from `@zion/sdk-contracts`, never re-declared (06a D-2). The producer's own
 * `AuthProvidersController` imports the same symbols and `zion-ops` is the third consumer, so a
 * local copy would be a second declaration of a shared contract that can disagree with it.
 */
export type {
  CreateIdpProviderDto,
  IdentityIssuerConfig,
  IdentityIssuerKeyVersion,
  IdpProvider,
  IdpProviderCatalogEntry,
  IdpProviderConfig,
  IdpProviderConfigField,
  UpdateIdpProviderDto,
};

export const AUTH_PROVIDERS_PATH = '/v1/admin/auth-providers';
export const AUTH_PROVIDER_CATALOG_PATH = `${AUTH_PROVIDERS_PATH}/catalog`;

/**
 * `encodeURIComponent` on the id, as in every sibling module: it stops a traversal-shaped id from
 * moving the request out of the `/v1/admin/auth-providers` surface.
 */
export function authProviderPath(id: string): string {
  return `${AUTH_PROVIDERS_PATH}/${encodeURIComponent(id)}`;
}

/** I1: the provider-type catalog. Bare array. */
export function getProviderCatalog(): Promise<readonly IdpProviderCatalogEntry[]> {
  return api.get<readonly IdpProviderCatalogEntry[]>(AUTH_PROVIDER_CATALOG_PATH);
}

/** I2: the configured instances. Bare array. */
export function listAuthProviders(): Promise<readonly IdpProvider[]> {
  return api.get<readonly IdpProvider[]>(AUTH_PROVIDERS_PATH);
}

/** I3: 201. 409 when the type is already configured (`singletonPerOrg`). */
export function createAuthProvider(body: CreateIdpProviderDto): Promise<IdpProvider> {
  return api.post<IdpProvider>(AUTH_PROVIDERS_PATH, body);
}

/** I4: 200. A 503 here can be the upstream last-enabled rule; see the module note. */
export function updateAuthProvider(id: string, body: UpdateIdpProviderDto): Promise<IdpProvider> {
  return api.patch<IdpProvider>(authProviderPath(id), body);
}

/**
 * I5: `void`. The delete routes of both `AuthProvidersController` and the site-admin proxy
 * controller carry `@HttpCode(HttpStatus.NO_CONTENT)`, and the client resolves `undefined` for a
 * 204. A 503 here can be the upstream last-enabled rule; see the module note.
 */
export function deleteAuthProvider(id: string): Promise<void> {
  return api.delete<void>(authProviderPath(id));
}

/**
 * I6: the read-only settings of the CP identity token issuer (feature 2026-10-22-cp-identity-token,
 * D11, D12). This module writes the path whole, so `fixture-routes-answer.test.ts` enrols the route.
 * The value equals `IDENTITY_ISSUER_CONFIG_PATH` of `@zion/sdk-contracts`, and
 * `settings-api-paths.test.ts` pins that.
 */
export const IDENTITY_TOKEN_CONFIG_PATH = '/v1/admin/auth-providers/identity-token';

/**
 * {@link getIdentityTokenConfig} throws it when a 2xx answer of I6 does not parse with
 * `identityIssuerConfigSchema`.
 *
 * `message` names the first path and issue, for example
 * `The identity token answer did not match the contract: keyVersions: the first key version signs, and only the first`.
 * `useApiResource` shows it in the error state with no status, because the server sent no failure.
 */
export class IdentityTokenContractError extends Error {
  constructor(detail: string) {
    super(`The identity token answer did not match the contract: ${detail}`);
    this.name = 'IdentityTokenContractError';
  }
}

/**
 * I6: read the issuer settings.
 *
 * Who calls it: the Identity token page. Output: the answer, which the function parses with
 * `identityIssuerConfigSchema`. When the issuer setting is absent, it holds `enabled: false` with
 * null URLs and empty lists. Else it holds these values:
 * - the three URLs;
 * - the TTL;
 * - the signer;
 * - the audiences;
 * - the email domains;
 * - the key versions.
 *
 * Errors: an `ApiError` for a non-2xx answer (403 without `cp:idp:read`, 502 or 503
 * from the proxy), and an {@link IdentityTokenContractError} for a 2xx answer that does not parse.
 * Side effects: one GET.
 */
export async function getIdentityTokenConfig(): Promise<IdentityIssuerConfig> {
  const parsed = identityIssuerConfigSchema.safeParse(await api.get<unknown>(IDENTITY_TOKEN_CONFIG_PATH));
  if (parsed.success) return parsed.data;
  // A failed zod parse holds one issue at least; `slice` reads the first with no branch.
  const detail = parsed.error.issues
    .slice(0, 1)
    .map((issue) => `${issue.path.length === 0 ? 'answer' : issue.path.map(String).join('.')}: ${issue.message}`)
    .join('');
  throw new IdentityTokenContractError(detail);
}

export const IDP_READ_PERMISSION = 'cp:idp:read';
export const IDP_CREATE_PERMISSION = 'cp:idp:create';
export const IDP_UPDATE_PERMISSION = 'cp:idp:update';
export const IDP_DELETE_PERMISSION = 'cp:idp:delete';

/**
 * The producer's derived "a secret is stored for this field" key. It lives in an import-free module
 * that the dev fixture shares, so this package carries no third copy of the mask literal (module
 * note, 06a D-17).
 */
export { hasSecretFlagKey };

/**
 * `=== true`, on purpose. A legacy row that predates the flag reads as no stored secret, the safe
 * direction: the operator sees an empty field with no "a value is stored" claim, and a typed value
 * is a real write either way. A truthiness check would let any non-boolean value claim a
 * credential.
 */
export function hasStoredSecret(config: IdpProviderConfig, fieldKey: string): boolean {
  return config[hasSecretFlagKey(fieldKey)] === true;
}
